import React from "react";
import {
  User,
  Phone,
  MapPin,
  Map,
  MessageSquare,
  ChevronUp,
  ChevronDown,
  Link as LinkIcon,
  Download,
} from "lucide-react";
import { toast } from "react-toastify";
import ViewConversationButton from "./ViewConversationButton";
import OrderPaymentBadge from "./OrderPaymentBadge";
import { caseStyleFromItem, isBlackEdgeCaseStyle, orderHasBlackEdges, orderHasPersonalizedName } from "../../utils/orderLineDisplay";
import { NamedPieceBadge, OrderNameMark } from "./NamedPieceBadge";

const copyPhoneNumber = (phone) => {
  const text = String(phone || "");
  const notify = () => toast.success(`تم نسخ الرقم: ${text}`);
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(text).then(notify).catch(() => {
      toast.error("فشل نسخ الرقم");
    });
    return;
  }
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.style.position = "fixed";
  textArea.style.opacity = "0";
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  try {
    document.execCommand("copy");
    notify();
  } catch {
    toast.error("فشل نسخ الرقم");
  }
  document.body.removeChild(textArea);
};

const OrderMobileCardList = ({
  orders,
  expandedOrders,
  toggleExpand,
  borderClass = "border-purple-100",
  dateClass = "text-purple-600",
  productCardClass = "bg-teal-50 border-teal-100",
  productTitleClass = "text-teal-700",
  productLinkClass = "text-teal-600 hover:text-teal-800",
  onPhoneClick,
  renderActions,
  renderExpanded,
  onDownload,
  expandLabelCollapsed = "عرض المنتجات والحذف",
  expandLabelExpanded = "إخفاء المنتجات",
  conversationPreset,
  canSendConversationTemplates = true,
}) => {
  const renderPhone = (phone, isSecondary = false) => {
    const sizeClass = isSecondary ? "text-lg" : "text-xl";
    const commonClass =
      "flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-3 py-1 text-green-700 active:bg-green-100 transition w-full text-right";

    return (
      <button
        type="button"
        onClick={() => (onPhoneClick ? onPhoneClick(phone) : copyPhoneNumber(phone))}
        className={commonClass}
        title="اضغط لنسخ الرقم"
      >
        <Phone size={20} className="text-green-600 flex-shrink-0" />
        <span className={`${sizeClass} font-bold tracking-wide`} style={{ fontFamily: "monospace" }}>
          {phone}
        </span>
      </button>
    );
  };

  const isValidSecondPhone = (phone) =>
    phone && phone !== "غير معروف" && phone !== "Unknown Phone";

  const renderPhoneBlock = (order, phone, isSecondary = false) => (
    <div className="space-y-1.5">
      {conversationPreset ? (
        <ViewConversationButton
          order={order}
          phone={phone}
          templatePreset={conversationPreset}
          canSend={canSendConversationTemplates}
          compact
          className="w-full"
          label={isSecondary ? "عرض محادثة الرقم التاني" : "عرض المحادثة"}
        />
      ) : null}
      {renderPhone(phone, isSecondary)}
    </div>
  );

  return (
    <div className="flex flex-col gap-5 md:hidden">
      {orders.map((order) => {
        const isExpanded = expandedOrders.has(order.orderNumber);
        const namedOrder = orderHasPersonalizedName(order);
        const blackEdgeOrder = orderHasBlackEdges(order);
        return (
          <div
            key={order.orderNumber}
            className={`rounded-2xl border shadow-md overflow-hidden ${
              blackEdgeOrder
                ? "border-zinc-400 bg-zinc-100 ring-2 ring-zinc-200"
                : namedOrder
                  ? "border-fuchsia-400 bg-fuchsia-50 ring-2 ring-fuchsia-200"
                  : `bg-white ${borderClass}`
            }`}
          >
            {order.note ? (
              <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-start gap-2">
                <MessageSquare size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <p style={{ fontFamily: "cairo black" }} className="text-[17px] text-amber-800 leading-snug">
                  {order.note}
                </p>
              </div>
            ) : (
              <div className="bg-gray-50 border-b border-gray-100 px-4 py-2 flex items-center gap-2">
                <MessageSquare size={14} className="text-gray-300" />
                <p className="text-sm text-gray-400 italic">لا توجد ملاحظات</p>
              </div>
            )}

            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 font-mono font-bold text-gray-400 text-lg">
                  #{order.orderNumber}
                  <OrderNameMark order={order} />
                </span>
                <div className="flex flex-col items-end">
                  <span className={`text-lg ${dateClass}`}>{order.date}</span>
                  <OrderPaymentBadge order={order} />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <User size={15} className="text-gray-500" />
                <span className="font-semibold text-gray-800 text-lg">{order.name}</span>
              </div>

              <div className="space-y-3">
                {renderPhoneBlock(order, order.phoneNumber, false)}
                {isValidSecondPhone(order.phoneNumber2) &&
                  renderPhoneBlock(order, order.phoneNumber2, true)}
              </div>

              <div className="bg-gray-50 rounded-xl p-3 space-y-1">
                <div className="flex items-start gap-2">
                  <MapPin size={15} className="text-gray-500 mt-0.5 flex-shrink-0" />
                  <span style={{ fontFamily: "cairo black" }} className="text-[12px] text-gray-700 leading-snug">
                    {order.address}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Map size={15} className="text-purple-500" />
                  <span className="text-lg font-bold text-purple-600">{order.state}</span>
                </div>
              </div>

              {renderActions?.(order)}

              <button
                type="button"
                onClick={() => toggleExpand(order.orderNumber)}
                className="w-full flex items-center justify-between bg-green-50 rounded-xl px-3 py-2 border border-green-200 active:bg-green-100 transition"
              >
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronUp size={20} className="text-green-600" />
                  ) : (
                    <ChevronDown size={20} className="text-green-600" />
                  )}
                  <span className="text-gray-500 text-sm">
                    {isExpanded ? expandLabelExpanded : expandLabelCollapsed}
                  </span>
                </div>
                <span className="text-xl font-bold text-green-600">{order.total} ج.م</span>
              </button>

              {isExpanded && (
                <div className="space-y-3">
                  <div className="space-y-1">
                    {order.items.map((item, index) => (
                      <div
                        key={`${item.id}-${index}`}
                        className={`${
                          isBlackEdgeCaseStyle(caseStyleFromItem(item))
                            ? "bg-zinc-100 border-zinc-400 border-2"
                            : item.hasPersonalizedName
                              ? "bg-fuchsia-50 border-fuchsia-400 border-2"
                              : productCardClass
                        } rounded-lg p-1.5 border`}
                      >
                        <div className={`font-medium ${productTitleClass} text-xs leading-tight`}>{item.name}</div>
                        <NamedPieceBadge item={item} className="mt-1" />
                        <div style={{ fontFamily: "cairo black" }} className="text-[10px] text-gray-600 leading-tight">
                          {item.type}
                        </div>
                        <div className="flex items-center gap-1 mt-1 flex-wrap">
                          <span
                            className={`text-[10px] px-1.5 py-[2px] rounded font-mono ${
                              item.code?.startsWith("CUSTOM") ? "bg-green-100" : "bg-gray-100"
                            }`}
                          >
                            {item.code}
                          </span>
                          {item.driveLink && (
                            <div className="flex items-center gap-1">
                              <a
                                href={item.driveLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={productLinkClass}
                                title="فتح الرابط"
                              >
                                <LinkIcon size={12} />
                              </a>
                              {item.code?.startsWith("CUSTOM") && onDownload && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    onDownload(item.driveLink, `${order.orderNumber}-${item.id}.jpg`)
                                  }
                                  className="flex items-center gap-1 text-white px-2 py-[2px] rounded text-[10px]"
                                  style={{ backgroundColor: "#15d225" }}
                                >
                                  <Download size={10} />
                                  Download
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {renderExpanded?.(order)}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default OrderMobileCardList;
